// OIDC configuration for the cloud SPA, loaded at runtime from
// `/api/config`. Per CLOUD_TECHNICAL_DESIGN.md decision #2 (dedicated
// per-customer deployment), every customer has its own Cognito user
// pool + app client. A single bundled SPA image must therefore learn
// its Cognito coordinates at boot, not at build time.
//
// Public client + PKCE: no client secret in the browser.

import type { AuthProviderProps } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import { generateAndStoreSessionId } from './session';

interface RuntimeConfigDto {
  oidc_authority: string;
  oidc_client_id: string;
  cognito_hosted_ui_domain: string | null;
  ga4_measurement_id?: string;
}

export interface AuthConfig {
  oidcProviderProps: AuthProviderProps;
  cognitoLogoutUrl: () => string;
  /** GA4 measurement id from the same boot fetch; null on every instance that
   *  does not set one, which keeps consent and analytics off entirely. */
  analyticsMeasurementId: string | null;
}

let cachedAuthConfig: AuthConfig | null = null;

/** Load runtime config from `/api/config` and assemble OIDC + logout
 *  helpers. Caches on success — safe to call multiple times during
 *  boot. Throws on network failure or missing required fields, so the
 *  caller can render a configuration-error splash instead of mounting
 *  an unauthenticated SPA. */
export async function loadAuthConfig(): Promise<AuthConfig> {
  if (cachedAuthConfig) return cachedAuthConfig;

  const res = await fetch('/api/config');
  if (!res.ok) {
    throw new Error(`Failed to load /api/config: HTTP ${res.status}`);
  }
  const data = (await res.json()) as RuntimeConfigDto;

  if (!data.oidc_authority || !data.oidc_client_id) {
    throw new Error(
      'Runtime config missing OIDC settings — backend env vars ' +
        'TRES_API_OIDC_ISSUER and TRES_API_OIDC_AUDIENCE must be set.',
    );
  }

  const siteOrigin = window.location.origin;
  const clientId = data.oidc_client_id;
  const hostedUiDomain = data.cognito_hosted_ui_domain;

  const oidcProviderProps: AuthProviderProps = {
    authority: data.oidc_authority,
    client_id: clientId,
    redirect_uri: `${siteOrigin}/auth/callback`,
    post_logout_redirect_uri: `${siteOrigin}/`,
    response_type: 'code',
    scope: 'openid email profile',
    // Tokens in sessionStorage — cleared on tab close. localStorage
    // would survive across tabs but expands the XSS surface; this is
    // the documented compromise.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    // Strip ?code=&state= from the URL after a successful sign-in.
    // Also mint a fresh session_id so single-session enforcement
    // (CLOUD_TECHNICAL_DESIGN.md §3.2) considers this a new sign-in.
    onSigninCallback: () => {
      window.history.replaceState({}, document.title, window.location.pathname);
      generateAndStoreSessionId();
    },
  };

  // Cognito's OIDC discovery doc does not advertise an
  // `end_session_endpoint`, so react-oidc-context's signoutRedirect()
  // can't drive the upstream session out. The two-step pattern:
  //   1. clear the local oidc-client-ts user (auth.removeUser())
  //   2. redirect to the URL this returns — Cognito kills the SSO
  //      cookie and bounces back to logout_uri.
  const cognitoLogoutUrl = (): string => {
    if (!hostedUiDomain) {
      // No hosted-UI domain configured on the backend. Best we can do
      // is land the user back at the SPA home; the upstream Cognito
      // session remains, which means a re-sign-in just re-uses the
      // existing SSO cookie. Production deployments should always set
      // TRES_API_COGNITO_DOMAIN.
      console.warn(
        'cognito_hosted_ui_domain not configured — falling back to local logout only',
      );
      return `${siteOrigin}/`;
    }
    const params = new URLSearchParams({
      client_id: clientId,
      logout_uri: `${siteOrigin}/`,
    });
    return `https://${hostedUiDomain}/logout?${params.toString()}`;
  };

  cachedAuthConfig = {
    oidcProviderProps,
    cognitoLogoutUrl,
    analyticsMeasurementId: data.ga4_measurement_id ?? null,
  };
  return cachedAuthConfig;
}

// Sign-out is a two-navigation dance: clear the local OIDC user, then hit
// Cognito's /logout. Between the two, RequireAuth sees "unauthenticated"
// and fires its auto signinRedirect — a navigation race the sign-in
// sometimes wins, and because the Cognito SSO cookie is still alive at
// that moment, the user is silently signed straight back in (the
// 2026-08-06 "can't sign out"). This latch tells the gate to stand down;
// the page unloads moments later, so it never needs resetting.
let signingOut = false;

export function beginSignOut(): void {
  signingOut = true;
}

export function isSigningOut(): boolean {
  return signingOut;
}

/** Synchronous accessor for code that runs after `loadAuthConfig()`
 *  has resolved (i.e., anything inside the React tree once it's
 *  mounted). Throws if accessed before boot completed — that would
 *  indicate a code-ordering bug. */
export function authConfig(): AuthConfig {
  if (!cachedAuthConfig) {
    throw new Error('authConfig() accessed before loadAuthConfig() resolved');
  }
  return cachedAuthConfig;
}

/** Convenience re-export for existing callers — works because the
 *  cached config is populated by `loadAuthConfig()` before any React
 *  component renders. */
export const cognitoLogoutUrl = (): string => authConfig().cognitoLogoutUrl();

export const analyticsMeasurementId = (): string | null => authConfig().analyticsMeasurementId;
